import { useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { useAuth } from 'context/AuthContext';
import { Box, Container, IconButton, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import PatientsIcon from 'icons/PatientsIcon';
import { dateToString, toTitleCase } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { useGetUsers } from 'hooks/useUsers';
import { IPhotoSize } from 'types/types';
import PageHeader from 'components/common/PageHeader/PageHeader';
import useGlobalStyles from 'styles/globalStyles';
import PersonIcon from '@mui/icons-material/Person';
import UserStatus from 'components/statuses/UserStatus';
import { ListProductSuggestionsDto } from 'types/dto/ListProductSuggestionsDto';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import UserActionButtons from 'components/tables/ActionButtons/UserActionButtons';
import { currencies, tableColumnProps } from 'common/constants';
import ProductSuggestionsFilters from '../Filters/ProductSuggestionsFilters';
import { ProductSuggestionsFilterDto } from 'types/dto/ProductSuggestionsFilterDto';
import { useGetProductSuggestions } from 'hooks/useProductSuggestion';
import SmallImagesList from '../SmallImagesList/SmallImagesList';
import ProductSuggestionActionButtons from '../ActionButtons/ProductSuggestionActionButtons';
import ProductsIconOutline from 'icons/ProductsIconOutline';
import { snackbarService } from 'services/snackbarService';
import { copyToClipboard as copy } from 'common/utils';



interface Props {
  // Shows soft-deleted suggestions (auto-rejected as not personal care) with
  // a Restore action, instead of the normal active list.
  deletedView?: boolean;
}

export default function ProductSuggestionsTable({ deletedView = false }: Props) {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState<ProductSuggestionsFilterDto>({
    id: '',
    barcode: '',
    emptyCategory: false,
  });

  const requestParams: ListProductSuggestionsDto = {
    page: page + 1,
    size,
    ...filters,
    deleted: deletedView,
  };

  const { data, refetch, isLoading, isRefetching } = useGetProductSuggestions(requestParams, {
    refetchOnWindowFocus: false,
    onSuccess: () => { },
    onError: () => console.log('error'),
  });


  const copyToClipboard = async (value: string, label: string) => {
    if (!value) return;

    if (await copy(value)) {
      snackbarService.showSnackbar(`${label} copied`, 'success');
      return;
    }

    snackbarService.showSnackbar(`Could not copy ${label.toLowerCase()}`, 'error');
  }

  const columns: GridColDef[] = [
    {
      field: 'barcode',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Barcode" />
      ),
      renderCell: (params) => {
        return (
          <Box
            onClick={() => copyToClipboard(params.row.barcode, 'Barcode')}
            sx={{ cursor: 'pointer' }}
            title="Click to copy"
          >
            <CustomCellValue text={params.row.barcode} />
          </Box >
        )
      }
    },
    {
      field: 'id',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="ID" />
      ),
      renderCell: (params) => {
        return (
          <Box
            onClick={() => copyToClipboard(params.row.id, 'ID')}
            sx={{ cursor: 'pointer' }}
            title="Click to copy"
          >
            <CustomCellValue text={params.row.id} />
          </Box >
        )
      }
    },
    {
      field: 'createdAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Date" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={dateToString(new Date(params.row.createdAt.split('T')[0]))} />
          </Box >
        )
      }
    },
    {
      field: 'price',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Price" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${currencies.shekel}${params.row.price.toFixed(2)}`} />
          </Box >
        )
      }
    },
    {
      field: 'images',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Images" />
      ),
      renderCell: (params) => {
        return (
          <SmallImagesList
            images={params.row.mediaFiles}
            setCarouselImages={setCarouselImages}
            setInitialIndex={setCarouselIndex}
            setCarouselOpen={setCarouselOpen}
          />
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ProductSuggestionActionButtons
              id={params.row.id}
              refetch={refetch}
              isProductAdded={params.row.isProductAdded}
              deletedView={deletedView}
            />
          </Box>
        )
      }
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <ProductsIconOutline size={24} color={colors.primary} />
            </Box>
            <CustomText text={deletedView ? 'Deleted Suggestions' : 'All Suggestions'} fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <ProductSuggestionsFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
            refetch();
          }}
          onReset={() => {
            setFilters({ id: '', barcode: '', emptyCategory: false });
            setPage(0);
            refetch();
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          // checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading || isRefetching}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
